import React, { createContext, useState, useCallback, ReactNode, useContext } from "react";

// Entity types that support quick create
export type QuickCreateEntityType = "vehicle" | "sales-customer" | "supplier" | null;

interface QuickCreateContextType {
  activeDialog: QuickCreateEntityType;
  openCreateDialog: (entityType: QuickCreateEntityType) => void;
  closeCreateDialog: () => void;
  isDialogOpen: boolean;
}

const defaultContextValue: QuickCreateContextType = {
  activeDialog: null,
  openCreateDialog: () => {},
  closeCreateDialog: () => {},
  isDialogOpen: false,
};

export const QuickCreateContext = createContext<QuickCreateContextType>(defaultContextValue);

interface QuickCreateProviderProps {
  children: ReactNode;
}

export const QuickCreateProvider: React.FC<QuickCreateProviderProps> = ({ children }) => {
  const [activeDialog, setActiveDialog] = useState<QuickCreateEntityType>(null);

  const openCreateDialog = useCallback((entityType: QuickCreateEntityType) => {
    setActiveDialog(entityType);
  }, []);

  const closeCreateDialog = useCallback(() => {
    setActiveDialog(null);
  }, []);

  const value: QuickCreateContextType = {
    activeDialog,
    openCreateDialog,
    closeCreateDialog,
    isDialogOpen: activeDialog !== null,
  };

  return (
    <QuickCreateContext.Provider value={value}>
      {children}
    </QuickCreateContext.Provider>
  );
};

// Custom hook for easy access
export const useQuickCreate = () => {
  const context = useContext(QuickCreateContext);
  if (!context) {
    throw new Error("useQuickCreate must be used within a QuickCreateProvider");
  }
  return context;
};
